import { useCallback, useState } from 'react';
import { useCreateRecord } from 'src/api/APIHooks';
import Notifier from 'common/helpers/Notifier';
import { get, uniq, without } from 'lodash';
import { usePendingNotificationCount } from 'src/components/Navigation/hooks/usePendingNotifications';

const useInvoiceExports = ({ currentEmployeeId, fetchAllInvoices }) => {
  const { incrementPendingNotificationCount } = usePendingNotificationCount();
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [allInvoicesLoading, setAllInvoicesLoading] = useState(false);
  const deselectAll = useCallback(() => setSelectedInvoiceIds([]));
  const selectInvoices = useCallback((...invoiceIds) => {
    setSelectedInvoiceIds(uniq([...selectedInvoiceIds, ...invoiceIds]));
  });
  const deselectInvoices = useCallback((...invoiceIds) => {
    setSelectedInvoiceIds(without(selectedInvoiceIds, ...invoiceIds));
  });
  const selectAll = useCallback(async () => {
    setAllInvoicesLoading(true);
    const allInvoices = await fetchAllInvoices();
    const allInvoiceIds = get(allInvoices, 'data.data', []).map((data) => data.id);
    selectInvoices(...allInvoiceIds);
    setAllInvoicesLoading(false);
  }, [fetchAllInvoices]);
  const { createRecord: createExport } = useCreateRecord('export');
  const downloadInvoices = useCallback(async (include_attachments = true) => {
    try {
      await createExport({
        export_type: 'invoices',
        requester: currentEmployeeId,
        state: 'requested',
        details: {
          include_attachments,
          invoice_ids: selectedInvoiceIds,
        },
      });
      incrementPendingNotificationCount('export');
      Notifier.dispatch('information', "Export file is processing. You'll receive a notification when it's ready.");
    } catch (error) {
      window.datadogLogs.logger.error('Failed to request export', { error });
      Notifier.dispatch('error', 'There was a problem requesting your export.');
    }
  }, [createExport, currentEmployeeId, selectedInvoiceIds]);
  return {
    allInvoicesLoading,
    deselectAll,
    deselectInvoices,
    downloadInvoices,
    selectAll,
    selectInvoices,
    selectedInvoiceIds,
  };
};

export default useInvoiceExports;
